import {
  getEvolutionApiUrl,
  getEvolutionApiKey,
  getEvolutionInstance,
  getN8nWebhookUrl,
} from "./admin-config";
import { getSupabase } from "./supabase";

// ═══════════════════════════════════════════════════
// SUPABASE STORAGE — Marketing images
// ═══════════════════════════════════════════════════

const SUPABASE_URL =
  import.meta.env.VITE_SUPABASE_URL ||
  "https://kgowevpumysppcwkbqxn.supabase.co";
const MARKETING_BUCKET = "marketing-assets";
const MARKETING_IMAGES = [
  "POST-1.png",
  "POST-2.png",
  "POST-3.png",
  "POST-4.png",
  "POST-5.png",
  "POST-6.png",
  "POST-7.png",
  "POST-8.png",
  "POST-9.png",
  "POST-10.png",
  "POST-11.png",
  "POST-12.png",
];

/**
 * Obtiene la URL pública de una imagen de marketing aleatoria.
 * @returns {string} URL pública de la imagen
 */
export function getRandomMarketingImageUrl() {
  const img =
    MARKETING_IMAGES[Math.floor(Math.random() * MARKETING_IMAGES.length)];
  return `${SUPABASE_URL}/storage/v1/object/public/${MARKETING_BUCKET}/${img}`;
}

// ═══════════════════════════════════════════════════
// MENSAJES DE RECORDATORIO (templates)
// ═══════════════════════════════════════════════════

/**
 * Genera el texto de recordatorio según el número (1, 2 o 3).
 * @param {number} reminderNumber - 1, 2 o 3
 * @param {string} nombre - Nombre del prospecto
 * @returns {string} Mensaje de recordatorio
 */
export function getReminderMessage(reminderNumber, nombre = "estimado/a") {
  const n = nombre.trim() || "estimado/a";
  switch (reminderNumber) {
    case 1:
      return `¡Hola ${n}! 👋 Somos del Colegio Tepeyac. Notamos que no hemos podido continuar nuestra plática. ¿Sigues interesado/a en conocer más sobre nuestros programas educativos? Estamos aquí para resolver cualquier duda que tengas. 😊`;
    case 2:
      return `¡Hola ${n}! 🌟 Del Colegio Tepeyac, queríamos darte un último recordatorio. Nos encantaría ayudarte a encontrar la mejor opción educativa para tu familia. Si ya no estás interesado/a, no te preocupes. ¡Te deseamos lo mejor! 💛`;
    case 3:
      return `¡Hola ${n}! 😊 Ha pasado un tiempo desde que hablamos. En el Colegio Tepeyac seguimos con inscripciones abiertas y tenemos novedades que podrían interesarte. ¿Te gustaría retomar la conversación? ¡Estamos para servirte! 🎓`;
    default:
      return `¡Hola ${n}! 👋 Del Colegio Tepeyac. ¿Sigues interesado/a en conocer más sobre nuestros programas educativos?`;
  }
}

// ═══════════════════════════════════════════════════
// EVOLUTION API — Envío de mensajes
// ═══════════════════════════════════════════════════

export async function sendWhatsAppMessage({ phone, message, wa_jid, leadId, instance }) {
  try {
    const baseUrl = getEvolutionApiUrl();
    const finalInstance = instance || getEvolutionInstance();
    // Limpiar el número de teléfono
    let cleanPhone = phone;
    if (wa_jid && wa_jid.includes("@")) {
      cleanPhone = wa_jid.split("@")[0];
    }
    cleanPhone = cleanPhone.replace(/\D/g, "");

    const apiKey = getEvolutionApiKey();
    const url = `${baseUrl}/message/sendText/${finalInstance}`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: apiKey,
      },
      body: JSON.stringify({
        number: cleanPhone,
        text: message,
        delay: 1200,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Evolution API error (${response.status}): ${errorText}`);
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error("Error enviando mensaje a Evolution API:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Elimina un mensaje del WhatsApp del lead (delete-for-everyone) vía Evolution API.
 * @param {Object} params
 * @param {string} params.wa_message_id - ID del mensaje en WhatsApp (key.id de Evolution)
 * @param {string} params.wa_jid - JID del chat (ej: 5213312345678@s.whatsapp.net)
 * @param {boolean} [params.fromMe=true] - Si el mensaje fue enviado por nosotros
 */
export async function deleteWhatsAppMessage({ wa_message_id, wa_jid, fromMe = true, instance }) {
  if (!wa_message_id) throw new Error("wa_message_id requerido")
  if (!wa_jid) throw new Error("wa_jid requerido")

  const baseUrl = getEvolutionApiUrl()
  const finalInstance = instance || getEvolutionInstance()
  const apiKey = getEvolutionApiKey()
  const url = `${baseUrl}/chat/deleteMessageForEveryone/${finalInstance}`

  const response = await fetch(url, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      apikey: apiKey,
    },
    body: JSON.stringify({
      id: wa_message_id,
      remoteJid: wa_jid,
      fromMe,
    }),
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Evolution delete error (${response.status}): ${errorText}`)
  }

  return response.json().catch(() => ({ success: true }))
}

/**
 * Edita un mensaje ya enviado en WhatsApp del lead vía Evolution API.
 * WhatsApp solo permite editar mensajes propios dentro de los primeros 15 min.
 * Si pasó ese plazo, Evolution responde con error y el frontend cae al modo
 * "solo edita en el CRM".
 */
export async function editWhatsAppMessage({ wa_message_id, wa_jid, newText, fromMe = true, instance }) {
  if (!wa_message_id) throw new Error("wa_message_id requerido")
  if (!wa_jid) throw new Error("wa_jid requerido")
  if (!newText?.trim()) throw new Error("Texto nuevo vacío")

  const baseUrl = getEvolutionApiUrl()
  const finalInstance = instance || getEvolutionInstance()
  const apiKey = getEvolutionApiKey()
  const url = `${baseUrl}/chat/updateMessage/${finalInstance}`

  // Evolution v2 valida que `number` coincida con `key.remoteJid`. Hay que
  // mandar el JID completo (5213317...@s.whatsapp.net), no solo los dígitos.
  const fullJid = wa_jid.includes("@")
    ? wa_jid
    : `${String(wa_jid).replace(/\D/g, "")}@s.whatsapp.net`

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: apiKey,
    },
    body: JSON.stringify({
      number: fullJid,
      text: newText.trim(),
      key: {
        remoteJid: fullJid,
        fromMe,
        id: wa_message_id,
      },
    }),
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Evolution edit error (${response.status}): ${errorText}`)
  }

  return response.json().catch(() => ({ success: true }))
}

/**
 * Notificar a n8n sobre cambio de estado del bot
 */
export async function toggleBotStatus({ leadId, phone, enabled }) {
  try {
    const webhookUrl = getN8nWebhookUrl();
    if (!webhookUrl)
      return {
        success: false,
        error: new Error("N8N Webhook URL no configurada"),
      };
    const response = await fetch(webhookUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        action: "toggle_bot",
        leadId: leadId,
        phone: phone,
        botEnabled: enabled,
        timestamp: new Date().toISOString(),
      }),
    });

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error("Error actualizando estado del bot:", error);
    return { success: false, error };
  }
}

export async function notifyLeadStatusChange({
  leadId,
  phone,
  newStatus,
  oldStatus,
}) {
  try {
    const webhookUrl = getN8nWebhookUrl();
    if (!webhookUrl)
      return {
        success: false,
        error: new Error("N8N Webhook URL no configurada"),
      };
    const response = await fetch(webhookUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        action: "status_change",
        leadId: leadId,
        phone: phone,
        newStatus: newStatus,
        oldStatus: oldStatus,
        timestamp: new Date().toISOString(),
      }),
    });

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error("Error notificando cambio de estado:", error);
    return { success: false, error };
  }
}

// ═══════════════════════════════════════════════════
// EVOLUTION API — Envío de imagen con caption
// ═══════════════════════════════════════════════════

/**
 * Envía una imagen con caption por WhatsApp vía Evolution API.
 * @param {{ phone: string, imageUrl: string, caption: string, wa_jid?: string, leadId?: string }} params
 * @returns {Promise<{ success: boolean, data?: any, error?: string }>}
 */
export async function sendWhatsAppImage({
  phone,
  imageUrl,
  caption,
  wa_jid,
  leadId,
  instance,
}) {
  try {
    const baseUrl = getEvolutionApiUrl();
    const finalInstance = instance || getEvolutionInstance();
    const apiKey = getEvolutionApiKey();

    if (!baseUrl || !finalInstance || !apiKey) {
      console.warn("[sendWhatsAppImage] Falta configuración de Evolution API");
      return { success: false, error: "Evolution API no configurada" };
    }

    // Limpiar número
    let cleanPhone = phone;
    if (wa_jid && wa_jid.includes("@")) {
      cleanPhone = wa_jid.split("@")[0];
    }
    cleanPhone = cleanPhone.replace(/\D/g, "");

    const url = `${baseUrl}/message/sendMedia/${finalInstance}`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: apiKey,
      },
      body: JSON.stringify({
        number: cleanPhone,
        mediatype: "image",
        media: imageUrl,
        caption: caption,
        delay: 1200,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Evolution API media error (${response.status}): ${errorText}`,
      );
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error("❌ Error enviando imagen por WhatsApp:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Envia un documento (PDF) via Evolution API.
 */
export async function sendWhatsAppDocument({
  phone,
  documentUrl,
  caption,
  fileName,
  wa_jid,
  instance,
}) {
  try {
    const baseUrl = getEvolutionApiUrl();
    const finalInstance = instance || getEvolutionInstance();
    const apiKey = getEvolutionApiKey();

    if (!baseUrl || !finalInstance || !apiKey) {
      return { success: false, error: "Evolution API no configurada" };
    }

    let cleanPhone = phone;
    if (wa_jid && wa_jid.includes("@")) {
      cleanPhone = wa_jid.split("@")[0];
    }
    cleanPhone = cleanPhone.replace(/\D/g, "");

    const url = `${baseUrl}/message/sendMedia/${finalInstance}`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: apiKey,
      },
      body: JSON.stringify({
        number: cleanPhone,
        mediatype: "document",
        media: documentUrl,
        caption: caption || "",
        fileName: fileName || "documento.pdf",
        delay: 1200,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Evolution API document error (${response.status}): ${errorText}`,
      );
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error("[sendWhatsAppDocument] Error:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Envía un recordatorio completo (imagen + texto) a un lead.
 * @param {{ lead: Object, reminderNumber: number }} params
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export async function sendReminderToLead({ lead, reminderNumber }) {
  const phone = lead?.telefono;
  const wa_jid = lead?.wa_jid;
  const nombre = lead?.nombre_tutor || "estimado/a";

  if (!phone && !wa_jid) {
    return { success: false, error: "Sin número de teléfono" };
  }

  const imageUrl = getRandomMarketingImageUrl();
  const caption = getReminderMessage(reminderNumber, nombre);

  // Intentar enviar imagen con caption — usa la instancia del lead si existe
  const imgResult = await sendWhatsAppImage({
    phone: phone || wa_jid?.split("@")[0] || "",
    imageUrl,
    caption,
    wa_jid,
    leadId: lead?.id,
    instance: lead?.instancia,
  });

  // Si falla la imagen, enviar solo texto como fallback
  if (!imgResult.success) {
    console.warn(
      `⚠️ Imagen falló para recordatorio ${reminderNumber}, enviando solo texto...`,
    );
    const textResult = await sendWhatsAppMessage({
      phone: phone || wa_jid?.split("@")[0] || "",
      message: caption,
      wa_jid,
      leadId: lead?.id,
      instance: lead?.instancia,
    });
    return textResult;
  }

  return imgResult;
}

// ═══════════════════════════════════════════════════
// CHECK WHATSAPP NUMBER — Evolution API v2
// POST /chat/whatsappNumbers/{instance}
// ═══════════════════════════════════════════════════

// ═══════════════════════════════════════════════════
// FETCH WHATSAPP PROFILE PICTURE — Evolution API v2
// POST /chat/fetchProfilePictureUrl/{instance}
// ═══════════════════════════════════════════════════

// In-memory set to track JIDs already being fetched this session (dedup)
const _picFetchInFlight = new Set()

export async function fetchWhatsAppProfilePic(waJid, instance) {
  if (!waJid) return null

  const baseUrl = getEvolutionApiUrl();
  const finalInstance = instance || getEvolutionInstance();
  const apiKey = getEvolutionApiKey();
  if (!baseUrl || !finalInstance || !apiKey) return null

  // Clean JID: try both raw and without @s.whatsapp.net suffix
  const variants = [waJid]
  const cleanNum = waJid.replace(/@.*$/, "")
  if (cleanNum !== waJid) variants.push(cleanNum)

  for (const number of variants) {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        if (attempt > 0) await new Promise(r => setTimeout(r, 1500))

        const res = await fetch(`${baseUrl}/chat/fetchProfilePictureUrl/${finalInstance}`, {
          method: "POST",
          headers: { "Content-Type": "application/json", apikey: apiKey },
          body: JSON.stringify({ number }),
        })

        if (!res.ok) continue

        const data = await res.json()
        const url = data?.profilePictureUrl || data?.url || data?.picture || null
        if (url) return url
      } catch {
        continue
      }
    }
  }

  return null
}

/**
 * Fetch profile pic from Evolution API and persist to DB.
 * Only calls the API if lead.foto_perfil is empty.
 * Returns the URL (from DB or freshly fetched).
 */
export async function getOrFetchProfilePic(lead) {
  if (!lead?.id) return null

  // Already has a saved pic — use it
  if (lead.foto_perfil) return lead.foto_perfil

  const waJid = lead.wa_jid
  if (!waJid) return null

  // Prevent duplicate in-flight requests for the same JID this session
  if (_picFetchInFlight.has(waJid)) return null
  _picFetchInFlight.add(waJid)

  const url = await fetchWhatsAppProfilePic(waJid, lead.instancia)

  if (url) {
    // Save to DB so we never fetch again for this lead
    try {
      await getSupabase().from("leads").update({ foto_perfil: url }).eq("id", lead.id)
    } catch (e) {
      console.warn("[profilePic] Error saving to DB:", e.message)
    }
  } else {
    // Failed — remove from Set so it can be retried in the future
    _picFetchInFlight.delete(waJid)
  }

  return url
}

export async function checkWhatsAppNumber(phone) {
  try {
    const baseUrl = getEvolutionApiUrl();
    const instance = getEvolutionInstance();
    const apiKey = getEvolutionApiKey();

    if (!baseUrl || !instance || !apiKey) {
      return { exists: null, error: "Evolution API no configurada" };
    }

    // Clean phone: remove spaces, dashes, parens, +
    let clean = String(phone).replace(/[\s\-\(\)\+]/g, "");
    // If starts with 0, remove it
    if (clean.startsWith("0")) clean = clean.slice(1);
    // If 10 digits (Mexican), add 52
    if (/^\d{10}$/.test(clean)) clean = "52" + clean;

    const res = await fetch(`${baseUrl}/chat/whatsappNumbers/${instance}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", apikey: apiKey },
      body: JSON.stringify({ numbers: [clean] }),
    });

    if (!res.ok) {
      return { exists: null, error: `Error ${res.status}` };
    }

    const data = await res.json();
    // Response: [{ exists: bool, jid: string, number: string }]
    const result = Array.isArray(data) ? data[0] : data;

    return {
      exists: result?.exists ?? false,
      jid: result?.jid || null,
      number: result?.number || clean,
      error: null,
    };
  } catch (err) {
    return { exists: null, error: err.message };
  }
}
